/**
 * Classify an `<a href>` emitted by marked.parse() as external URL,
 * in-page anchor, or a file-path navigation target to hand off to the
 * CodeViewer (`open-file-viewer`).
 *
 * Used by MarkdownContent to upgrade plain markdown links like
 * `[archive_2026_Q2.md](archive_2026_Q2.md)` into workspace file opens
 * instead of full-page browser navigations (which 404).
 */

const WORKSPACE_ROOT = '/home/akrsmv/projects';
const PROTOCOL_RE = /^[a-z][a-z0-9+.-]*:/i;

export type ResolvedHref =
  | { type: 'external' }
  | { type: 'anchor' }
  | { type: 'file'; path: string };

export function resolveMarkdownHref(rawHref: string | null | undefined, basePath?: string): ResolvedHref {
  if (!rawHref) return { type: 'anchor' };
  const href = rawHref.trim();
  if (!href) return { type: 'anchor' };

  if (href.startsWith('#')) return { type: 'anchor' };
  if (PROTOCOL_RE.test(href)) return { type: 'external' };

  // Drop fragment + query — we resolve to a file path
  const [withoutFrag] = href.split('#');
  const [cleanPath] = withoutFrag.split('?');
  if (!cleanPath) return { type: 'anchor' };

  // Absolute filesystem path
  if (cleanPath.startsWith('/')) {
    if (cleanPath === WORKSPACE_ROOT || cleanPath.startsWith(WORKSPACE_ROOT + '/')) {
      return { type: 'file', path: cleanPath };
    }
    return { type: 'external' };
  }

  const base = basePath && basePath.startsWith('/') ? basePath : WORKSPACE_ROOT;

  // Workspace-relative heuristic: if the first path segment matches a
  // directory name already present as an ancestor of `base`, treat the
  // href as workspace-relative (common in cross-project doc links).
  const firstSeg = cleanPath.split('/')[0];
  const isExplicitRelative = cleanPath.startsWith('./') || cleanPath.startsWith('../');
  if (firstSeg && !isExplicitRelative && base.includes(`/${firstSeg}/`)) {
    return { type: 'file', path: joinPath(WORKSPACE_ROOT, cleanPath) };
  }

  return { type: 'file', path: joinPath(base, cleanPath) };
}

function joinPath(base: string, relative: string): string {
  const baseParts = base.split('/').filter(Boolean);
  const relParts = relative.split('/');
  for (const part of relParts) {
    if (part === '' || part === '.') continue;
    if (part === '..') {
      if (baseParts.length > 0) baseParts.pop();
      continue;
    }
    baseParts.push(part);
  }
  return '/' + baseParts.join('/');
}

export function dirname(absPath: string): string {
  const idx = absPath.lastIndexOf('/');
  if (idx <= 0) return '/';
  return absPath.slice(0, idx);
}
